import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Organization, OrgOption } from '../../../../core/models/model';
import { GlobalSettingsService } from '../../../../core/services/global-settings.service';
import { IllumLiveService } from '../../../../core/services/illum-live.service';
import { forkJoin } from 'rxjs';
import _ from 'lodash';
import moment from 'moment';
import { SessionOptionsService } from '../../../../core/services/session-option.service';
import { ArcAngelPlaybackModeService } from '../../../../core/services/arc-angel-playback-mode.service';
import { TimelineService } from '../../../../core/services/timeline.service';
import { IncidentCategoryService } from '../../../../core/services/incident-category.service';
import { ArcAngelMonitorModeService } from '../../arc-angel-monitor-mode.service';
import { ArcAngelLiveModeService } from '../../../../core/services/arc-angel-live-mode.service';

@Component({
  selector: 'app-playback-mode-menu',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './playback-mode-menu.component.html',
  styleUrl: './playback-mode-menu.component.css'
})

export class PlaybackModeMenuComponent implements OnInit {
  showLiveModeOptions = false;
  showPlaybackModeOptions = false;
  searchText = '';
  organizations: any[] = [];
  filteredOrganizations: Organization[] = [];
  selectedOrg: any = null;
  activeTab: string = 'incidents';
  years = [2024, 2023, 2022];
  selectedYear = 2024;
  orgs: { [key: string]: OrgOption } = {};
  orgsSorted: OrgOption[] = [];
  incidentData: any;
  procallData: any;
  trackingData: any;
  events: any[] = [];
  playbackDataLoaded = false;
  selectedEvent: any;
  PLAYBACK_MODE : "Playback Mode" | undefined
  playbackTrees = {
    incidents: {} as { [orgId: string]: any },
    procalls: {} as { [orgId: string]: any },
    tracking: {} as { [orgId: string]: any }
  };
  currentIncidentPlaybackTree: any;
  currentProcallPlaybackTree: any;
  currentTrackingPlaybackTree: any;
  selectedEventId: string | null = null;
  constructor(public activeModal: NgbActiveModal,
    private globalSettings: GlobalSettingsService,
    private illumLiveService: IllumLiveService,
    private sessionOptionsService: SessionOptionsService,
    private arcAngelPlaybackModeService: ArcAngelPlaybackModeService,
    private timelineService: TimelineService,
    private incidentCategoryService: IncidentCategoryService,
    private arcAngelMonitorModeService: ArcAngelMonitorModeService,
    private arcAngelLiveModeService: ArcAngelLiveModeService
  ) {}


   ngOnInit() {
    // Set default tab based on mode
    if (this.showLiveModeOptions) {
      this.activeTab = 'liveIncidentsTab';
    } else if (this.showPlaybackModeOptions) {
      this.activeTab = 'incidents';
    }
    this.organizations = this.globalSettings.getOrgTree();
    this.sortAndFilterOrganizations();
    this.createTree();
   }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['organizations']) {
      this.sortAndFilterOrganizations();
    }
  }

  sortAndFilterOrganizations() {
    // Sort alphabetically
    let orgs = [...this.organizations].sort((a, b) =>
      (a.name?.toLowerCase() || '').localeCompare(b.name?.toLowerCase() || '')
    );
    this.filteredOrganizations = orgs;
  }

  searchOrgs() {
    this.sortAndFilterOrganizations();
  }

  selectOrg(org: any) {
    this.selectedOrg = org;
  }

  playback() {
    this.activeModal.close('Playback Mode');
  }
  
  goHome() {
    this.activeModal.close();
  }

createTree() {
  this.illumLiveService.getUserPermissionsAndOrgIds().subscribe(data => {
    const orgOptions: OrgOption[] = [];
    this.orgs = {};

    data.myOrgs.forEach((org: any) => {
      if (org.type?.toLowerCase() !== 'msp') {
        const orgOption = new OrgOption(org.name, org.id);
        orgOptions.push(orgOption);
        this.orgs[orgOption.getId()] = orgOption;
      }
    });

    // Order orgs
    this.orgsSorted = Object.values(this.orgs);
    this.orgsSorted = _.sortBy(this.orgsSorted, ['name']);
    this.orgsSorted = _.orderBy(this.orgsSorted, (o: any) => o.name?.toLowerCase());
    this.orgsSorted = _.orderBy(this.orgsSorted, (o: any) => parseInt(o.orgId, 10));

    // Parallel API calls
    forkJoin({
      incidentDataByOrg: this.illumLiveService.getAllIncidentsForPlaybackTree(),
      procalls: this.illumLiveService.getAllProcallsForPlaybackTree(),
      tracking: this.illumLiveService.getAllActiveTrackingEventsForPlaybackTree()
    }).subscribe(response => {
      const procallData = response.procalls;
      const trackingData = response.tracking;

      // Flatten and collect org IDs
      const orgIdsWithIncident = _.uniq(_.map(_.flatten(response.incidentDataByOrg), 'organization'));
      const orgIdsWithProcall = _.uniq(_.map(_.flatten(response.procalls), 'organizationId'));
      const orgIdsWithTracking = _.uniq(Object.values(response.tracking).map((org: any) => org.managingOrganization));
      const orgIdsAll = _.uniq(_.concat(orgIdsWithIncident,orgIdsWithProcall,orgIdsWithTracking));
      // Filter orgsSorted
      if (orgIdsAll.length > 0) {
        this.orgsSorted = this.orgsSorted.filter((o: any) => orgIdsAll.includes(o.orgId));
      }

      // Save data for later use
      this.incidentData = response.incidentDataByOrg;
      this.procallData = procallData;
      this.trackingData = trackingData;

      // Build a flat events array from all sources
      this.events = [];
      // Incidents
      for (const orgIncidents of this.incidentData) {
        for (const incident of orgIncidents) {
          this.events.push({
            ...incident,
            type: 'incident',
            organizationId: incident.organization // or whatever property matches your org
          });
        }
      }
      // Procalls
      for (const procall of this.procallData) {
        this.events.push({
          ...procall,
          type: 'procall',
          organizationId: procall.organizationId
        });
      }
      // Tracking
      for (const key of Object.keys(this.trackingData)) {
        const tracking = this.trackingData[key];
        this.events.push({
          ...tracking,
          type: 'tracking',
          organizationId: tracking.managingOrganization
        });
      }

      // Sort events if you have them
      this.events.sort((a, b) => b.unixStartDate - a.unixStartDate);
      Object.values(this.orgs).forEach((org: any) => {
        org.setEvents(this.events);
      });

      this.playbackDataLoaded = true;
      if (this.orgsSorted.length > 0) {
        this.setSelectedOrg(this.orgsSorted[0]);
      }
    }, error => {
      console.error('Error getting incident data', error);
      // Show error dialog if needed
    });
  });
}

getIncidentEvents() {
  return this.selectedOrg?.getEvents?.().filter((e: any) => e.type === 'incident') || [];
}
getProcallEvents() {
  return this.selectedOrg?.getEvents?.().filter((e: any) => e.type === 'procall') || [];
}
getTrackingEvents() {
  return this.selectedOrg?.getEvents?.().filter((e: any) => e.type === 'tracking') || [];
}

setSelectedOrg(item: OrgOption) {
  this.selectedOrg = item;
  const selOrgId = item.getId();

  if (!this.playbackTrees.incidents[selOrgId]) {
    this.playbackTrees.incidents[selOrgId] = this.createTreeForIncident(this.incidentData, selOrgId);
  }
  if (!this.playbackTrees.procalls[selOrgId]) {
    this.playbackTrees.procalls[selOrgId] = this.createTreeForProcall(this.procallData, selOrgId);
  }
  if (!this.playbackTrees.tracking[selOrgId]) {
    this.playbackTrees.tracking[selOrgId] = this.createTreeForActiveTracking(this.trackingData, selOrgId);
  }

  this.currentIncidentPlaybackTree = this.playbackTrees.incidents[selOrgId];
  this.currentProcallPlaybackTree = this.playbackTrees.procalls[selOrgId];
  this.currentTrackingPlaybackTree = this.playbackTrees.tracking[selOrgId];
}

setSelectedEvent(node: any) {
    this.selectedEventId = node?.event?.id ?? null;
    this.selectedEvent = node.event || node;
  }
getMonthDateRange(year: number, month: number) {
  const start = moment({ year, month, day: 1 });
  const end = start.clone().endOf('month');
  return { start, end };
}

createTreeForIncident(data: any, selOrgId: string) {
  const tree: any[] = [];
  for (let year = moment().year(); year >= 2018; year--) {
    let yearCount = 0;
    const yearEntry = {
      name: year,
      child: false,
      children: [] as any[],
      unixStartDate: year,
      count: 0,
      expanded: false
    };
    tree.push(yearEntry);

    for (let month = 11; month >= 0; month--) {
      let monthCount = 0;
      if (year === moment().year() && month > moment().month()) continue;

      const monthRange = this.getMonthDateRange(year, month);
      const name = monthRange.start.format('MMMM') + ' ' + year;
      const monthNode = {
        name,
        child: false,
        children: [] as any[],
        key: selOrgId + monthRange.start.format('MMMMYYYY'),
        unixStartDate: month,
        count: 0,
        expanded: false
      };
      yearEntry.children.push(monthNode);

      // Find the correct data index for this org
      let indexToUse: number | null = null;
      for (let i = 0; i < data.length; i++) {
        if (data[i].length > 0 && data[i][0].organization === selOrgId) {
          indexToUse = i;
        }
      }

      if (indexToUse !== null) {
        for (let i = 0; i < data[indexToUse].length; i++) {
          const incident = data[indexToUse][i];
          if (
            moment(incident.createdAt).year() === year &&
            moment(incident.createdAt).month() === month
          ) {
            // You may want to create an EventOption here if you have that class
            const description = incident.description?.replace('Incident Reported at ', '') || '';
            const treeNode = {
              name: `${incident.label} / ${moment(incident.createdAt).format('MMMM Do YYYY, h:mm:ss a')} / ${description}`,
              child: true,
              class: 'childnodetext',
              children: [],
              event: incident,
              unixStartDate: moment(incident.createdAt).unix()
            };
            monthNode.children.push(treeNode);
            monthCount++;
          }
        }
      }
      monthNode.count = monthCount;
      yearCount += monthCount;
    }
    yearEntry.count = yearCount;
  }
  return tree;
}

createTreeForProcall(data: any[], selOrgId: string) {
  const tree: any[] = [];
  for (let year = moment().year(); year >= 2018; year--) {
    let yearCount = 0;
    const yearEntry = {
      name: year,
      child: false,
      children: [] as any[],
      unixStartDate: year,
      count: 0
    };
    tree.push(yearEntry);

    for (let month = 11; month >= 0; month--) {
      let monthCount = 0;
      if (year === moment().year() && month > moment().month()) continue;

      const monthRange = this.getMonthDateRange(year, month);
      const name = monthRange.start.format('MMMM') + ' ' + year;
      const monthNode = {
        name,
        child: false,
        children: [] as any[],
        key: selOrgId + monthRange.start.format('MMMMYYYY'),
        unixStartDate: month,
        count: 0
      };
      yearEntry.children.push(monthNode);

      for (let i = 0; i < data.length; i++) {
        const procall = data[i];
        if (
          moment(procall.createdAt).year() === year &&
          moment(procall.createdAt).month() === month &&
          procall.organizationId === selOrgId
        ) {
          const title = procall.title || 'Unknown';
          const treeNode = {
            name: `${title} ${moment(procall.createdAt).format('MMMM Do YYYY, h:mm:ss a')}`,
            child: true,
            class: 'childnodetext',
            children: [],
            event: procall,
            unixStartDate: moment(procall.createdAt).unix()
          };
          monthNode.children.push(treeNode);
          monthCount++;
        }
      }
      monthNode.count = monthCount;
      yearCount += monthCount;
    }
    yearEntry.count = yearCount;
  }
  return tree;
}

createTreeForActiveTracking(data: any, selOrgId: string) {
 let trackingArray: any[] = [];
  if (Array.isArray(data)) {
    trackingArray = data;
  } else if (data && typeof data === 'object') {
    trackingArray = Object.values(data);
  }

  const tree: any[] = [];
  for (let year = moment().year(); year >= 2018; year--) {
    let yearCount = 0;
    const yearEntry = {
      name: year,
      child: false,
      children: [] as any[],
      unixStartDate: year,
      count: 0
    };
    tree.push(yearEntry);

    for (let month = 11; month >= 0; month--) {
      let monthCount = 0;
      if (year === moment().year() && month > moment().month()) continue;

      const monthRange = this.getMonthDateRange(year, month);
      const name = monthRange.start.format('MMMM') + ' ' + year;
      const monthNode = {
        name,
        child: false,
        children: [] as any[],
        key: selOrgId + monthRange.start.format('MMMMYYYY'),
        unixStartDate: month,
        count: 0
      };
      yearEntry.children.push(monthNode);

      for (let i = 0; i < trackingArray.length; i++) {
        const tracking = trackingArray[i];
        if (
          moment(tracking.createdAt).year() === year &&
          moment(tracking.createdAt).month() === month &&
          tracking.managingOrganization === selOrgId
        ) {
          const title = tracking.title || 'Unknown';
          const treeNode = {
            name: `${title} ${moment(tracking.begin).format('MMMM Do YYYY, h:mm:ss a')}`,
            child: true,
            class: 'childnodetext',
            children: [],
            event: tracking,
            unixStartDate: moment(tracking.begin).unix()
          };
          monthNode.children.push(treeNode);
          monthCount++;
        }
      }
      monthNode.count = monthCount;
      yearCount += monthCount;
    }
    yearEntry.count = yearCount;
  }
  return tree;
}
 playbackEventClicked(){
  this.sessionOptionsService.setMode("Playback Mode");
  this.sessionOptionsService.setSessionData(this.sessionOptionsService.SessionOption( "Playback Mode", this.selectedEvent));
  this.activeModal.close('Playback Mode');
	this.timelineService.setTimelineByMode("Playback Mode");
  const eventId = this.selectedEvent.id || this.selectedEvent.eventId;
  if (eventId && eventId !== 'undefined') {
    if(this.activeTab === 'procalls') {
      this.arcAngelPlaybackModeService.startProcallPlaybackMode(eventId, this);
    }else{
      this.arcAngelPlaybackModeService.startIncidentPlaybackMode(eventId, this);
    }
  } else {
    console.error('[playbackEventClicked] Invalid eventId:', eventId);
    alert('Invalid event ID. Cannot start playback.');
  }
};

liveEventClicked() {
  this.sessionOptionsService.setSessionData(
    this.sessionOptionsService.SessionOption('Live Mode', this.selectedEvent)
  );
  const orgId = this.arcAngelMonitorModeService.getOrgIdForLiveIncident(this.selectedEvent.id ?? '');
  this.illumLiveService.setManagingOrgId(orgId ?? '0');
  this.incidentCategoryService.setManagingOrgId(orgId ?? '0');
  this.sessionOptionsService.goToLiveIncidentMode(this.selectedEvent.id ?? '', true, true);
  this.arcAngelLiveModeService.startLiveIncidentMode(this.selectedEvent.id ?? '', this);
  this.activeModal.close('Live Mode');
}

filterIncidentsByMode(incidents: any[], mode: string): any[] {
  const LIVE_MODE = 'Live Mode';
  const PLAYBACK_MODE = 'Playback Mode';
  const INCIDENT = 'incident';

  if (!incidents) return [];
  if (mode === LIVE_MODE) {
    return incidents.filter(
      value => (value.eventType === INCIDENT || value.type === INCIDENT) && value.status === 'active'
    );
  } else if (mode === PLAYBACK_MODE) {
    return incidents.filter(
      value => (value.eventType === INCIDENT || value.type === INCIDENT) && (value.status === 'active' || value.status === 'inactive')
    );
  }
  return [];
}

filterProcallsByMode(procalls: any[], mode: string): any[] {
  const LIVE_MODE = 'Live Mode';
  const PLAYBACK_MODE = 'Playback Mode';
  const PROCALL = 'procall'; // or whatever your eventType/type value is

  if (!procalls) return [];
  if (mode === LIVE_MODE) {
    return procalls.filter(
      value =>
        (value.eventType === PROCALL || value.type === PROCALL) &&
        (value.status === 'open' || value.status === 'pending')
    );
  } else if (mode === PLAYBACK_MODE) {
    return procalls.filter(
      value =>
        (value.eventType === PROCALL || value.type === PROCALL) &&
        value.status === 'resolved'
    );
  }
  return [];
}

filterTrackingByMode(trackings: any[], mode: string): any[] {
  const LIVE_MODE = 'Live Mode';
  const PLAYBACK_MODE = 'Playback Mode';
  const TRACKING = 'tracking'; // or whatever your eventType/type value is

  if (!trackings) return [];
  if (mode === PLAYBACK_MODE) {
    return trackings.filter(
      value =>
        (value.eventType === TRACKING || value.type === TRACKING) &&
        value.userCheckIn
    );
  } else if (mode === LIVE_MODE) {
    return trackings.filter(
      value =>
        (value.eventType === TRACKING || value.type === TRACKING) &&
        value.status !== 'RESOLVED'
    );
  }
  return [];
}

formatEventDate(date: any): string {
  return date ? moment(date).format('MMMM Do YYYY, h:mm:ss a') : '';
}

  filterNodes(nodes: any[] | undefined): any[] {
    if (!nodes || nodes.length === 0) return [];
    const q = (this.searchText ?? '').toString().trim().toLowerCase();
    if (!q) return nodes;

    const out: any[] = [];
    for (const n of nodes) {
      if (this.nodeMatches(n, q)) {
        out.push(n);
      }
    }
    return out;
  }

  private nodeMatches(node: any, q: string): boolean {
    if (!node) return false;
    const name = (node.name ?? '').toString().toLowerCase();
    if (name.includes(q)) return true;

    const eventName = (node?.event?.name ?? node?.event?.label ?? '').toString().toLowerCase();
    if (eventName.includes(q)) return true;

    if (Array.isArray(node.children) && node.children.length) {
      for (const child of node.children) {
        if (this.nodeMatches(child, q)) return true;
      }
    }

    return false;
  }
}
